import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Home, BookOpen, Settings as SettingsIcon, LogOut, HelpCircle, ChevronDown,
         School, Users, GraduationCap, FileSpreadsheet, Layers, Headset, ClipboardCheck, Code2, Database, Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Logo } from "./Logo";
import { useAuth } from "../context/AuthContext";
import { LanguageSwitcher } from "./LanguageSwitcher";

export const Sidebar = ({ role, open = false, onClose = () => {} }) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const TEACHER_NAV = [
    { key: "dashboard",        to: "/teacher/dashboard",       icon: Home },
    { key: "learning_modules", to: "/teacher/modules",         icon: BookOpen },
    { key: "manage_modules",   to: "/teacher/manage/modules",  icon: Layers },
    { key: "settings",         to: "/teacher/settings",        icon: SettingsIcon },
  ];
  const STUDENT_NAV = [
    { key: "dashboard",        to: "/student/dashboard", icon: Home },
    { key: "learning_modules", to: "/student/modules",   icon: BookOpen },
    { key: "quiz",             to: "/student/quiz",      icon: HelpCircle },
    { key: "settings",         to: "/student/settings",  icon: SettingsIcon },
  ];
  const ADMIN_NAV = [
    { key: "dashboard",        to: "/admin/dashboard",     icon: Home },
    { key: "schools",          to: "/admin/schools",       icon: School },
    { key: "teachers",         to: "/admin/teachers",      icon: GraduationCap },
    { key: "students",         to: "/admin/students",      icon: Users },
    { key: "learning_modules", to: "/admin/modules",       icon: BookOpen },
    { key: "bulk_import",      to: "/admin/import",        icon: FileSpreadsheet },
    { key: "backup_restore",   to: "/admin/backup",        icon: Database },
    { key: "vr_sessions",      to: "/admin/vr-sessions",   icon: Headset },
    { key: "vr_quiz_results",  to: "/admin/vr-quiz",       icon: ClipboardCheck },
    { key: "developer_docs",   to: "/admin/developer-docs",icon: Code2 },
  ];
  const PARENT_NAV = [{ key: "dashboard", to: "/parent/dashboard", icon: Home }];

  const NAV_FOR = { teacher: TEACHER_NAV, student: STUDENT_NAV, admin: ADMIN_NAV, parent: PARENT_NAV };
  const nav = NAV_FOR[role] || [];

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const initials = (user?.full_name || "U")
    .split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const subtitle = role === "student" ? (user?.class_label || "Student") : t(`sidebar.titles.${role}`);

  // Language switcher only for teacher & student (per user choice)
  const showLangSwitcher = role === "teacher" || role === "student";

  return (
    <>
      {/* Mobile backdrop */}
      <div
        onClick={onClose}
        data-testid="sidebar-backdrop"
        className={`fixed inset-0 z-30 bg-slate-900/50 backdrop-blur-sm transition-opacity lg:hidden ${
          open ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
      />
      <aside
        className={`sidebar-scroll fixed left-0 top-0 z-40 flex h-screen w-64 flex-col overflow-y-auto bg-[#0B1120] text-white transition-transform duration-300 lg:translate-x-0 ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="sidebar"
      >
      <div className="flex items-center justify-between px-6 pb-4 pt-7">
        <Logo size={36} />
        <button onClick={onClose} data-testid="sidebar-close-btn" className="text-slate-300 hover:text-white lg:hidden">
          <X size={22} />
        </button>
      </div>

      <nav className="mt-2 flex-1 space-y-1 px-4">
        {nav.map(({ key, to, icon: Icon }) => (
          <NavLink
            key={to}
            to={to}
            onClick={onClose}
            data-testid={`sidebar-link-${key.replace(/_/g, '-')}`}
            className={({ isActive }) =>
              `group flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all ${
                isActive
                  ? "bg-indigo-600 text-white shadow-lg shadow-indigo-900/30"
                  : "text-slate-300 hover:bg-white/5 hover:text-white"
              }`
            }
            end={to.endsWith("/dashboard")}
          >
            <Icon size={18} strokeWidth={2} />
            {t(`sidebar.nav.${key}`)}
          </NavLink>
        ))}
      </nav>

      {showLangSwitcher && (
        <div className="px-4 pb-2 pt-1">
          <LanguageSwitcher variant="sidebar" className="w-full justify-start" />
        </div>
      )}

      <div className="mt-auto border-t border-white/5 px-4 py-4">
        <div className="mb-2 flex items-center gap-3 rounded-xl bg-white/5 px-3 py-2.5">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-600 text-sm font-semibold text-white">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-white">{user?.full_name}</p>
            <p className="truncate text-xs text-slate-400">{subtitle}</p>
          </div>
          <ChevronDown size={16} className="text-slate-400" />
        </div>
        <button
          onClick={handleLogout}
          data-testid="sidebar-logout-btn"
          className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-slate-300 transition-colors hover:bg-white/5 hover:text-white"
        >
          <LogOut size={18} />
          {t("sidebar.logout")}
        </button>
      </div>
      </aside>
    </>
  );
};

export const AppShell = ({ role, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar role={role} open={open} onClose={() => setOpen(false)} />
      {/* Mobile top bar */}
      <header className="sticky top-0 z-20 flex items-center gap-3 border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur lg:hidden">
        <button
          onClick={() => setOpen(true)}
          data-testid="mobile-menu-btn"
          aria-label="Open menu"
          className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-50"
        >
          <Menu size={20} />
        </button>
        <Logo size={30} />
      </header>
      <main className="p-4 sm:p-6 lg:ml-64 lg:p-10">{children}</main>
    </div>
  );
};
